import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import landscapeContactPersonService from '../services/landscape-contact-person.service';
import { LandscapeContactPersonResponseDto } from '../dto/response/landscape-contact-person-response.dto';
import { LandscapeContactPersonSaveRequestDto } from '../dto/request/landscape-contact-person-save-request.dto';
import { LandscapeContactPersonUpdateRequestDto } from '../dto/request/landscape-contact-person-update-request.dto';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { AxiosError } from 'axios';

const initialState = {
  status: 'idle',
  landscapeContactPersons: [] as LandscapeContactPersonResponseDto[] | null,
  landscapeContactPerson: {} as LandscapeContactPersonResponseDto | null,
  error: null as string | null,
};

// export const fetchLandscapeContactPersons = createAsyncThunk(
//   'landscapeContactPersons/fetchLandscapeContactPersons',
//   async () => {
//     return await landscapeContactPersonService.fetchLandscapeContactPersons();
//   }
// );

export const fetchLandscapeContactPersonById = createAsyncThunk(
  'landscapeContactPersons/fetchLandscapeContactPersonById',
  async (id: string) => {
    return await landscapeContactPersonService.fetchLandscapeContactPersonById(
      id
    );
  }
);

export const fetchLandscapeContactPersonByLandscapeId = createAsyncThunk(
  'landscapeContactPersons/fetchLandscapeContactPersonByLandscapeId',
  async (landscapeId: string) => {
    return await landscapeContactPersonService.fetchLandscapeContactPersonsByLandscapeId(
      landscapeId
    );
  }
);

export const fetchLandscapeContactPersonByProjectId = createAsyncThunk(
  'landscapeContactPersons/fetchLandscapeContactPersonByProjectId',
  async (projectId: string) => {
    return await landscapeContactPersonService.fetchLandscapeContactPersonsByProjectId(
      projectId
    );
  }
);

export const saveLandscapeContactPerson = createAsyncThunk(
  'landscapeContactPersons/saveLandscapeContactPerson',
  async (saveRequest: LandscapeContactPersonSaveRequestDto) => {
    return await landscapeContactPersonService.saveLandscapeContactPerson(
      saveRequest
    );
  }
);

export const updateLandscapeContactPerson = createAsyncThunk(
  'landscapeContactPersons/updateLandscapeContactPerson',
  async (updateRequest: LandscapeContactPersonUpdateRequestDto) => {
    return await landscapeContactPersonService.updateLandscapeContactPerson(
      updateRequest
    );
  }
);

export const deleteLandscapeContactPerson = createAsyncThunk(
  'landscapeContactPersons/deleteLandscapeContactPerson',
  async (existingId: string, { rejectWithValue }) => {
    try {
      return await landscapeContactPersonService.deleteLandscapeContactPerson(
        existingId
      );
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const landscapeContactPersonSlice = createSlice({
  name: 'landscapeContactPersonSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLandscapeContactPersonById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchLandscapeContactPersonById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.landscapeContactPerson = action.payload;
      })
      .addCase(fetchLandscapeContactPersonById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      // .addCase(fetchLandscapeContactPersons.pending, (state) => {
      //   state.status = 'loading';
      // })
      // .addCase(fetchLandscapeContactPersons.fulfilled, (state, action) => {
      //   state.status = 'succeeded';
      //   state.landscapeContactPersons = action.payload;
      // })
      // .addCase(fetchLandscapeContactPersons.rejected, (state, action) => {
      //   state.status = 'failed';
      //   state.error = action.error?.message ?? null;
      // })
      .addCase(fetchLandscapeContactPersonByLandscapeId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        fetchLandscapeContactPersonByLandscapeId.fulfilled,
        (state, action) => {
          state.status = 'succeeded';
          state.landscapeContactPersons = action.payload;
        }
      )
      .addCase(
        fetchLandscapeContactPersonByLandscapeId.rejected,
        (state, action) => {
          state.status = 'failed';
          state.error = action.error?.message ?? null;
        }
      )
      .addCase(fetchLandscapeContactPersonByProjectId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        fetchLandscapeContactPersonByProjectId.fulfilled,
        (state, action) => {
          state.status = 'succeeded';
          state.landscapeContactPersons = action.payload;
        }
      )
      .addCase(
        fetchLandscapeContactPersonByProjectId.rejected,
        (state, action) => {
          state.status = 'failed';
          state.error = action.error?.message ?? null;
        }
      )
      .addCase(saveLandscapeContactPerson.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveLandscapeContactPerson.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.landscapeContactPersons?.push(action.payload);
        showSuccess('Contact Person added successfully.');
      })
      .addCase(saveLandscapeContactPerson.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(updateLandscapeContactPerson.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateLandscapeContactPerson.fulfilled, (state, action) => {
        state.status = 'succeeded';
        showSuccess('Contact Person saved successfully.');
        if (state.landscapeContactPersons) {
          const landscapeContactPersonIndex =
            state.landscapeContactPersons.findIndex(
              (landscapeContactPerson) =>
                landscapeContactPerson.id === action.payload.id
            );
          if (landscapeContactPersonIndex !== -1) {
            state.landscapeContactPersons[landscapeContactPersonIndex] =
              action.payload;
          }
        }
      })
      .addCase(updateLandscapeContactPerson.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteLandscapeContactPerson.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteLandscapeContactPerson.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Contact Person deleted successfully.');
      })
      .addCase(deleteLandscapeContactPerson.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting landscape failed. ${action.payload as string}`);
      });
  },
});

export default landscapeContactPersonSlice.reducer;
